import { z } from "zod";
import type { WhiteboardScene } from "@/lib/whiteboard/schema";

// Element-level merge of a board's drawing, shared by the server (saves from
// several editors) and the live channel's catch-up log. The rules are the
// editor's own (Excalidraw's reconcileElements): every element carries a
// version, the higher one wins, and a tie goes to the lower versionNonce.
// A deletion is a version too, so it is kept as a tombstone: without it, a
// tab that has not yet heard about the deletion would bring the element back.

/** A deleted element: just enough to win against an older copy of it. */
export const whiteboardTombstoneSchema = z.object({
  id: z.string().min(1).max(200),
  version: z.number().int().nonnegative(),
  versionNonce: z.number().int(),
  /** Epoch milliseconds of the deletion, to forget old tombstones. */
  updated: z.number().nonnegative().finite(),
});

export type WhiteboardTombstone = z.infer<typeof whiteboardTombstoneSchema>;

/** Tombstones a save may carry; a long session deletes many shapes. */
export const MAX_WHITEBOARD_TOMBSTONES = 20_000;

/** Past this, a tab that still holds a deleted element may bring it back. */
export const TOMBSTONE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

type Element = WhiteboardScene["elements"][number];

type Versioned = { version: number; versionNonce?: unknown };

// The editor's own fields, read from its JSON; a missing or odd one sorts as the editor would.
const nonceSchema = z.number().catch(0);
const indexSchema = z.string().nullable().catch(null);
const fileIdSchema = z.string().nullable().catch(null);

const nonceOf = (element: Versioned) => nonceSchema.parse(element.versionNonce);

/** Whether `candidate` replaces `current`: a higher version, or the same one with a lower nonce. */
export function supersedes(candidate: Versioned, current: Versioned) {
  if (candidate.version !== current.version) return candidate.version > current.version;

  return nonceOf(candidate) < nonceOf(current);
}

/** A board's stored drawing: the live scene and what was deleted from it. */
export type StoredWhiteboardScene = WhiteboardScene & { deleted?: WhiteboardTombstone[] };

/** The stored drawing without its tombstones, as every reader but the merge sees it. */
export function liveScene(stored: StoredWhiteboardScene): WhiteboardScene {
  const { deleted: _deleted, ...scene } = stored;

  return scene;
}

function prune(tombstones: Iterable<WhiteboardTombstone>, now: number) {
  return [...tombstones]
    .filter((tombstone) => now - tombstone.updated < TOMBSTONE_TTL_MS)
    .sort((a, b) => b.updated - a.updated)
    .slice(0, MAX_WHITEBOARD_TOMBSTONES);
}

const indexOf = (element: Element) => indexSchema.parse(element.index);

/** The editor's stacking order (fractional indices); elements without one keep their place. */
function byStackingOrder(a: Element, b: Element) {
  const left = indexOf(a);
  const right = indexOf(b);

  if (left === null || right === null || left === right) return 0;

  return left < right ? -1 : 1;
}

/** Only the images the live elements still draw. */
function referencedFiles(
  elements: readonly Element[],
  files: Iterable<WhiteboardScene["files"][number]>,
) {
  const used = new Set<string>();

  for (const element of elements) {
    const fileId = element.type === "image" ? fileIdSchema.parse(element.fileId) : null;

    if (fileId) used.add(fileId);
  }

  return [...files].filter((file) => used.has(file.id));
}

/**
 * Merges a save into the stored drawing, element by element: what the saving
 * tab never saw (another editor's shapes) stays, and what it deleted goes. The
 * saved preferences (grid, snap) win as a whole.
 */
export function mergeWhiteboardScene(
  stored: StoredWhiteboardScene,
  incoming: WhiteboardScene,
  incomingDeleted: readonly WhiteboardTombstone[],
  now = Date.now(),
): StoredWhiteboardScene {
  type Winner = { live: Element } | { deleted: WhiteboardTombstone };

  const winners = new Map<string, Winner>();

  const offer = (id: string, candidate: Winner) => {
    const current = winners.get(id);
    const value = "live" in candidate ? candidate.live : candidate.deleted;

    if (!current || supersedes(value, "live" in current ? current.live : current.deleted))
      winners.set(id, candidate);
  };

  // The saving tab's order first, so its new shapes keep their place among equals.
  for (const element of incoming.elements) offer(element.id, { live: element });

  for (const tombstone of incomingDeleted) offer(tombstone.id, { deleted: tombstone });

  for (const element of stored.elements) offer(element.id, { live: element });

  for (const tombstone of stored.deleted ?? []) offer(tombstone.id, { deleted: tombstone });

  const elements: Element[] = [];
  const deleted: WhiteboardTombstone[] = [];

  for (const winner of winners.values()) {
    if ("live" in winner) elements.push(winner.live);
    else deleted.push(winner.deleted);
  }

  elements.sort(byStackingOrder);
  const files = new Map(stored.files.map((file) => [file.id, file]));

  for (const file of incoming.files) files.set(file.id, file);

  return {
    ...incoming,
    elements,
    files: referencedFiles(elements, files.values()),
    deleted: prune(deleted, now),
  };
}

/**
 * A whole-scene replacement (a save that predates the merge, checked against
 * the revision): what it no longer holds becomes a tombstone, so a live tab
 * still drawing on an older copy cannot bring it back.
 */
export function replaceWhiteboardScene(
  stored: StoredWhiteboardScene,
  incoming: WhiteboardScene,
  now = Date.now(),
): StoredWhiteboardScene {
  const kept = new Set(incoming.elements.map((element) => element.id));

  const deleted = new Map(
    (stored.deleted ?? []).filter((tombstone) => !kept.has(tombstone.id)).map((t) => [t.id, t]),
  );

  for (const element of stored.elements) {
    if (kept.has(element.id)) continue;
    deleted.set(element.id, {
      id: element.id,
      version: element.version + 1,
      versionNonce: 0,
      updated: now,
    });
  }

  return { ...incoming, deleted: prune(deleted.values(), now) };
}
